import React, {useEffect, useState} from 'react';
import './project.css';
import {useParams, useNavigate} from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import { assetData } from '../../data/asset-data';

import IconButton from '@mui/material/IconButton';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
// import YoutubeEmbed from '../../components/youtube-embed/youtube-embed';
// import DOMPurify from 'dompurify';

const Project = ({projects}) => {
  const navigate = useNavigate();
  const [currentAssetIndex, setCurrentAssetIndex] = useState(0);
  const [project, setProject] = useState(null);
  const [nextProjectId, setNextProjectId] = useState(null);
  const [prevProjectId, setPrevProjectId] = useState(null);
  // const [mobileLayout, setMobileLayout] = useState(window.innerWidth <= 950);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {id} = useParams();

  const selectProject = (id) => {
    navigate(`/project/${id}`);
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const getAssetById = (assetId) => {
    return assetData.find(asset => asset.id === assetId);
  }

  const getProjectAssets = () => {
    if (!project || !project.related_assets) return [];
    const assets = project.related_assets.map(assetId => getAssetById(assetId)).filter(Boolean);
    return assets;
  }

  const nextAsset = () => {
    const assets = getProjectAssets();
    if (assets.length > 1) {
      setCurrentAssetIndex((prevIndex) => 
        prevIndex === assets.length - 1 ? 0 : prevIndex + 1
      );
    }
  }

  const prevAsset = () => {
    const assets = getProjectAssets();
    if (assets.length > 1) {
      setCurrentAssetIndex((prevIndex) => 
        prevIndex === 0 ? assets.length - 1 : prevIndex - 1
      );
    }
  }

  const renderAsset = (asset) => {
    if (!asset) return null;

    if (asset.asset_type === 'video') {
      // Video assets are served from https://chris-mcgee.com/videos/
      const videoUrl = `https://chris-mcgee.com/videos/${asset.asset_url}`;
      
      return (
        <div className="video-container">
          <video 
            className="project-asset" 
            controls 
            autoPlay 
            muted
            loop
            onError={(e) => {
              console.error('Video loading error:', e);
              console.error('Video URL that failed:', videoUrl);
              // Show a fallback message
              const videoElement = e.target;
              const container = videoElement.parentElement;
              if (container) {
                container.innerHTML = `
                  <div class="video-fallback">
                    <div class="video-fallback-content">
                      <h3>Video Not Available</h3>
                      <p>${asset.title}</p>
                      <p>The video file "${asset.asset_url}" could not be loaded.</p>
                      <p>Please ensure the video is available at: ${videoUrl}</p>
                    </div>
                  </div>
                `;
              }
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    } else {
      return (
        <img 
          className="project-asset" 
          src={`/images/work/${asset.asset_url}`} 
          alt={asset.title}
          onError={(e) => console.error('Image loading error:', e)}
        />
      );
    }
  }

  useEffect(() => {
    let cleanId = parseInt(id);
    let proj = projects.find((p) => p.id === cleanId);
    setProject(proj);
    setCurrentAssetIndex(0); // Reset to first asset when project changes

    let next = (parseInt(id) + 1 > projects.length) ? 1 : parseInt(id) + 1;
    setNextProjectId(next);

    let prev = (id - 1 < 1) ? projects.length : id - 1;
    setPrevProjectId(prev);

    // const handleWindowResize = () => {
    //   if (window.innerWidth <= 950) {
    //     setMobileLayout(true);
    //   } else {
    //     setMobileLayout(false);
    //   }
    // };

    // window.addEventListener('resize', handleWindowResize);

    // return () => {
    //   window.removeEventListener('resize', handleWindowResize);
    // };
  }, [id, projects]);

  const assets = getProjectAssets();
  const currentAsset = assets[currentAssetIndex];
 
  return (
    <motion.div
      initial={{ 
        opacity: 0,
      }}
      animate={{ 
        opacity: 1,
      }}
      exit={{ 
        opacity: 0,
      }}
      transition={{ duration: 0.2 }}
    >
      { project && currentAsset &&
        <div className='project-navbar'>
          <IconButton onClick={() => selectProject(prevProjectId)} className="project-nav-arrow">
            <KeyboardDoubleArrowLeftIcon  
              sx={{ 
                color: 'white', 
                fontSize: '3rem' }} 
            />
          </IconButton>
          <div className='project-title-container'>
            <div className='project-title'>{project.name}</div>
            <IconButton onClick={handleOpenModal} className="project-info-button">
              <InfoIcon style={{ color: 'white', fontSize: '20px' }} />
            </IconButton>
          </div>
          <IconButton onClick={() => selectProject(nextProjectId)} className="project-nav-arrow">
            <KeyboardDoubleArrowRightIcon 
              sx={{ 
                color: 'white', 
                fontSize: '3rem' }} 
            />
          </IconButton>
        </div>
      }
      
      { project && currentAsset &&
        <div className='project-container'>
          <div className='project-content-container'>
            <div className='asset-viewer'>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentAsset.id}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="asset-container"
                >
                  {renderAsset(currentAsset)}
                  <div className="asset-title-overlay">
                    {currentAsset.title}
                  </div>
                </motion.div>
              </AnimatePresence>
              
              {assets.length > 1 && (
                <div className="carousel-controls">
                  <IconButton onClick={prevAsset} className="carousel-button">
                    <NavigateBeforeIcon />
                  </IconButton>
                  <div className="carousel-indicators">
                    {assets.map((_, index) => (
                      <div
                        key={index}
                        className={`carousel-indicator ${index === currentAssetIndex ? 'active' : ''}`}
                        onClick={() => setCurrentAssetIndex(index)}
                      />
                    ))}
                  </div>
                  <IconButton onClick={nextAsset} className="carousel-button">
                    <NavigateNextIcon />
                  </IconButton>
                </div>
              )}
            </div>
            
          </div>
          
        </div>
      }
      
      {/* Project Description Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="project-description-modal"
        aria-describedby="project-description-content"
      >
        <Box className="project-modal">
          <div className="modal-header">
            <h2>{project?.name}</h2>
            <IconButton onClick={handleCloseModal} className="modal-close-button">
              <CloseIcon />
            </IconButton>
          </div>
          <div 
            className="modal-content"
            dangerouslySetInnerHTML={{ __html: project?.description || '' }}
          />
        </Box>
      </Modal>
    </motion.div>
  );
}

export default Project;